
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusIcon, UploadIcon, EditIcon } from '../BorrowingsIcons';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DocumentUploadForm } from './DocumentUploadForm';
import { toast } from "sonner";

interface AddLoanDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  handleAddLoan: () => void;
}

export const AddLoanDialog: React.FC<AddLoanDialogProps> = ({ 
  open, 
  setOpen,
  handleAddLoan 
}) => {
  const [activeTab, setActiveTab] = useState<string>("upload");
  const [parsedData, setParsedData] = useState<any>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // Handle document upload and parsing
  const handleDocumentUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    // Show processing state
    setIsProcessing(true);
    
    // Simulate document processing
    setTimeout(() => {
      // Sample parsed data that would come from AI document parsing
      const parsedLoanData = {
        instrumentType: "term-loan",
        lender: "hdfc",
        amount: 5000000,
        currency: "inr",
        interestRate: 8.75,
        interestType: "floating",
        sanctionDate: new Date().toISOString().split('T')[0],
        maturityDate: new Date(new Date().setFullYear(new Date().getFullYear() + 5)).toISOString().split('T')[0],
        repaymentType: "emi",
        covenants: [
          { name: "DSCR", value: "1.5", frequency: "quarterly" },
          { name: "Current Ratio", value: "1.2", frequency: "quarterly" }
        ]
      };
      
      setParsedData(parsedLoanData);
      setActiveTab("manual");
      setIsProcessing(false);
      toast.success("Document parsed successfully! Please review the extracted details.");
    }, 2000);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="mr-2 h-4 w-4" />
          New Borrowing
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add New Borrowing</DialogTitle>
          <DialogDescription>
            Enter the details of the new borrowing or upload loan documents for automated extraction.
          </DialogDescription>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="upload" className="flex items-center gap-2">
              <UploadIcon className="h-4 w-4" />
              Upload Documents
            </TabsTrigger>
            <TabsTrigger value="manual" className="flex items-center gap-2">
              <EditIcon className="h-4 w-4" />
              Manual Entry
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="upload" className="pt-4">
            <DocumentUploadForm 
              isProcessing={isProcessing} 
              onUpload={handleDocumentUpload} 
            />
          </TabsContent>
          
          <TabsContent value="manual" className="pt-4">
            <div className="grid gap-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="instrument-type" className="text-right">
                  Instrument Type
                </Label>
                <div className="col-span-3">
                  <Select defaultValue={parsedData?.instrumentType || ""}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select instrument type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="term-loan">Term Loan</SelectItem>
                      <SelectItem value="working-capital">Working Capital</SelectItem>
                      <SelectItem value="ncd">Non-Convertible Debenture</SelectItem>
                      <SelectItem value="cp">Commercial Paper</SelectItem>
                      <SelectItem value="ecb">External Commercial Borrowing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="lender" className="text-right">
                  Lender
                </Label>
                <div className="col-span-3">
                  <Select defaultValue={parsedData?.lender || ""}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select lender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hdfc">HDFC Bank</SelectItem>
                      <SelectItem value="sbi">State Bank of India</SelectItem>
                      <SelectItem value="abc">Aditya Birla Capital</SelectItem>
                      <SelectItem value="franklin">Franklin Templeton MF</SelectItem>
                      <SelectItem value="new">Add New Lender...</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="amount" className="text-right">
                  Amount
                </Label>
                <Input 
                  id="amount" 
                  type="number" 
                  className="col-span-2" 
                  placeholder="Enter amount" 
                  defaultValue={parsedData?.amount || ""}
                />
                <Select defaultValue={parsedData?.currency || "inr"}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inr">INR</SelectItem>
                    <SelectItem value="usd">USD</SelectItem>
                    <SelectItem value="eur">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="interest-rate" className="text-right">
                  Interest Rate
                </Label>
                <Input 
                  id="interest-rate" 
                  type="number" 
                  step="0.01" 
                  className="col-span-1" 
                  placeholder="Rate %" 
                  defaultValue={parsedData?.interestRate || ""}
                />
                <div className="col-span-2">
                  <Select defaultValue={parsedData?.interestType || "fixed"}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Fixed</SelectItem>
                      <SelectItem value="floating">Floating</SelectItem>
                      <SelectItem value="benchmark">Benchmark-Linked</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="sanction-date" className="text-right">
                  Sanction Date
                </Label>
                <Input 
                  id="sanction-date" 
                  type="date" 
                  className="col-span-3" 
                  defaultValue={parsedData?.sanctionDate || ""}
                />
              </div>
              
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="maturity-date" className="text-right">
                  Maturity Date
                </Label>
                <Input 
                  id="maturity-date" 
                  type="date" 
                  className="col-span-3" 
                  defaultValue={parsedData?.maturityDate || ""}
                />
              </div>
              
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="repayment-type" className="text-right">
                  Repayment Type
                </Label>
                <div className="col-span-3">
                  <Select defaultValue={parsedData?.repaymentType || ""}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select repayment type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="emi">EMI</SelectItem>
                      <SelectItem value="equated-principal">Equated Principal</SelectItem>
                      <SelectItem value="interest-only">Interest Only, Principal at Maturity</SelectItem>
                      <SelectItem value="structured">Structured Repayment</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {parsedData?.covenants && parsedData.covenants.length > 0 && (
                <div className="mt-2 border rounded-md p-3 bg-muted/50">
                  <Label className="mb-2 block">Covenants Detected</Label>
                  <div className="space-y-2">
                    {parsedData.covenants.map((covenant: any, index: number) => (
                      <div key={index} className="flex gap-2 items-center text-sm">
                        <span className="font-medium">{covenant.name}:</span>
                        <span>{covenant.value}</span>
                        <span className="text-muted-foreground">({covenant.frequency})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
        
        <DialogFooter className="pt-4">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleAddLoan} disabled={isProcessing}>
            {isProcessing ? "Processing..." : "Save Borrowing"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
